import { Box, Flex, Heading, HStack, Text } from "@chakra-ui/react";
import moment from "moment";
import { useContext, useMemo } from "react";
import { CurrencyCodeEnum } from "src/api/generated/types";
import StatusTag from "src/components/styled/StatusTag";
import { useTheme } from "src/hooks/useTheme";
import { colors } from "src/theme";
import { D } from "src/utils/helpers";
import { FullServiceContext } from "./context";

interface QuarterData {
  quarter: number;
  status: "reviewed" | "in_progress" | "none";
  months: {
    name: string;
    date: string;
    capGains: number;
    income: number;
  }[];
}

export const Overview = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  const { client, monthlyPoints } = useContext(FullServiceContext);

  // Helper function to determine quarter status
  const determineQuarterStatus = (
    months: QuarterData["months"]
  ): QuarterData["status"] => {
    const currentDate = moment.utc();
    const quarterEndDate = moment.utc(months[months.length - 1].date);

    if (quarterEndDate.isBefore(currentDate, "month")) {
      return "reviewed";
    } else if (
      months.some((month) =>
        moment.utc(month.date).isSameOrBefore(currentDate, "month")
      )
    ) {
      return "in_progress";
    }
    return "none";
  };

  const quarters = useMemo(() => {
    if (!monthlyPoints) return [];

    // Group monthly points into quarters
    const groupedByQuarter = monthlyPoints.reduce((acc, point) => {
      const date = moment.utc(point.timestamp);
      const quarter = Math.floor(date.month() / 3) + 1;

      if (!acc[quarter]) {
        acc[quarter] = {
          quarter,
          status: "none", // Default status
          months: [],
        };
      }

      acc[quarter].months.push({
        name: date.format("MMM"),
        date: date.format("YYYY-MM-DD"),
        capGains: point.totalCapGainsCents,
        income: point.netIncomeCents,
      });

      return acc;
    }, {} as Record<number, QuarterData>);

    // Convert to array and sort by quarter
    return Object.values(groupedByQuarter)
      .sort((a, b) => a.quarter - b.quarter)
      .map((quarter) => ({
        ...quarter,
        status: determineQuarterStatus(quarter.months),
      }));
  }, [monthlyPoints]);

  return (
    <Box
      bg={theme.background}
      w="100%"
      border={`1px solid ${theme.border}`}
      borderRadius="lg"
      p="10px"
      pt="0"
      overflow="auto"
    >
      {/* <Heading size="md" mb="15px" mt="15px" color={theme.header}>
        Overview ({currentYear})
      </Heading> */}

      <Box display="flex" flexDirection="column" gap={0}>
        {quarters.map((quarter) => (
          <Box
            key={quarter.quarter}
            style={{
              marginTop: 0,
              borderBottom:
                quarter.quarter === 4 ? "none" : `1px solid ${theme.border}`,
              paddingBottom: 20,
              marginBottom: 0,
            }}
          >
            <HStack mb="20px" mt="20px" alignItems="center">
              <Heading margin="0" flex={1} size="sm" color={theme.text}>
                Q{quarter.quarter}
              </Heading>

              <Box>
                {quarter.status === "reviewed" && (
                  <StatusTag
                    type="success"
                    boxProps={{
                      style: { padding: "2px 7px", background: "transparent" },
                    }}
                    iconStyle={{
                      fontSize: 12,
                    }}
                    label="Reviewed"
                    iconName="fa-solid fa-check-circle"
                  />
                )}
                {quarter.status === "in_progress" && (
                  <StatusTag
                    boxProps={{
                      style: { padding: "2px 7px", background: "transparent" },
                    }}
                    iconStyle={{
                      fontSize: 12,
                    }}
                    type="warning"
                    label="In Progress"
                    iconName="fa-solid fa-spinner"
                  />
                )}
              </Box>
            </HStack>

            <Box>
              <Flex mb={2} gap={4}>
                <Box flexBasis="100px">
                  <Text
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="bold"
                    color={theme.text}
                  >
                    Month
                  </Text>
                </Box>

                <Box flexBasis="150px">
                  <Text
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="bold"
                    color={theme.text}
                  >
                    Income
                  </Text>
                </Box>

                <Box flexBasis="150px">
                  <Text
                    textTransform="uppercase"
                    fontSize="10px"
                    fontWeight="bold"
                    color={theme.text}
                  >
                    Cap Gains
                  </Text>
                </Box>
              </Flex>

              <Box display="flex" flexDirection="column" gap={1}>
                {quarter.months.map((month) => {
                  // if the month is not in this month or less, we want to show TBD as the number
                  const isFuture = moment(month.date)
                    .startOf("month")
                    .isAfter(moment());

                  return (
                    <Flex key={month.name} gap={4} alignItems="center">
                      <Box flexBasis="100px">
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={theme.header}
                        >
                          {month.name}
                        </Text>
                      </Box>

                      <Box flexBasis="150px">
                        {isFuture ? (
                          <Text fontSize="sm" color={theme.text}>
                            &mdash;
                          </Text>
                        ) : (
                          <NumberField
                            value={month.income}
                            currency={client?.currency || CurrencyCodeEnum.Usd}
                            showCaret={false}
                            color={theme.header}
                            fontWeight="medium"
                          />
                        )}
                      </Box>

                      <Box flexBasis="150px">
                        {isFuture ? (
                          <Text fontSize="sm" color={theme.text}>
                            &mdash;
                          </Text>
                        ) : (
                          <NumberField
                            value={month.capGains}
                            currency={client?.currency || CurrencyCodeEnum.Usd}
                            fontWeight="bold"
                          />
                        )}
                      </Box>
                    </Flex>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const NumberField = ({
  value,
  currency,
  showCaret = true,
  color,
  fontWeight = "semibold",
}: {
  value: number;
  currency: CurrencyCodeEnum;
  showCaret?: boolean;
  color?: string;
  fontWeight?: string;
}) => {
  const isNeg = value < 0;

  return (
    <HStack
      style={{
        alignItems: "center",
      }}
    >
      {showCaret ? (
        <i
          className={`fa-solid fa-caret-${value >= 0 ? "up" : "down"}`}
          style={{
            position: "relative",
            top: 1,
            color: value >= 0 ? colors.positive : colors.negative,
          }}
        />
      ) : (
        <i
          className="fa-solid fa-money-bill"
          style={{ color: color, fontSize: 10 }}
        ></i>
      )}
      <Text
        fontSize="sm"
        fontWeight={fontWeight}
        color={color || (value >= 0 ? colors.positive : colors.negative)}
      >
        {isNeg
          ? `(${D(Math.abs(value), currency).toFormat()})`
          : D(value, currency).toFormat()}
      </Text>
    </HStack>
  );
};
